export type FitzpatrickType = 1 | 2 | 3 | 4 | 5 | 6

export type SkinType = 'oily' | 'dry' | 'combination' | 'normal' | 'not-sure'

export type Sensitivity = 'not-sensitive' | 'slightly-sensitive' | 'very-sensitive'

export type SkinCondition =
  | 'none'
  | 'acne'
  | 'dryness'
  | 'oiliness'
  | 'sensitivity'
  | 'hyperpigmentation'
  | 'rosacea'
  | 'eczema'

export interface ShortSurveyData {
  age: number | null
  skinType: SkinType | null
  sensitivity: Sensitivity | null
  skinConditions: SkinCondition[]
}

export interface SurveyOption<T> {
  value: T
  label: string
  description?: string
}

export interface FitzpatrickOption {
  value: FitzpatrickType
  label: string
  color: string
  skinTone: string
  sunReaction: string
}

export const FITZPATRICK_OPTIONS: FitzpatrickOption[] = [
  {
    value: 1,
    label: 'Type I',
    color: '#F5D6C6',
    skinTone: 'Light, Pale White',
    sunReaction: 'Always burns, never tans',
  },
  {
    value: 2,
    label: 'Type II',
    color: '#E8C4A8',
    skinTone: 'White, Fair',
    sunReaction: 'Usually burns, tans with difficulty',
  },
  {
    value: 3,
    label: 'Type III',
    color: '#D4A574',
    skinTone: 'Medium White to Olive',
    sunReaction: 'Sometimes burns, gradually tans',
  },
  {
    value: 4,
    label: 'Type IV',
    color: '#B8864E',
    skinTone: 'Olive Tone',
    sunReaction: 'Rarely burns, tans with ease',
  },
  {
    value: 5,
    label: 'Type V',
    color: '#8B5A2B',
    skinTone: 'Light Brown',
    sunReaction: 'Very rarely burns, tans very easily',
  },
  {
    value: 6,
    label: 'Type VI',
    color: '#4A2912',
    skinTone: 'Dark Brown',
    sunReaction: 'Never burns, deeply pigmented',
  },
]

export const SKIN_TYPE_OPTIONS: SurveyOption<SkinType>[] = [
  { value: 'oily', label: 'Oily', description: 'Shiny, enlarged pores' },
  { value: 'dry', label: 'Dry', description: 'Tight, flaky, rough' },
  { value: 'combination', label: 'Combination', description: 'Oily T-zone, dry cheeks' },
  { value: 'normal', label: 'Normal', description: 'Balanced, few issues' },
  { value: 'not-sure', label: 'Not sure', description: "I don't know my skin type" },
]

export const SENSITIVITY_OPTIONS: SurveyOption<Sensitivity>[] = [
  { value: 'not-sensitive', label: 'Not sensitive', description: 'Rarely react to products' },
  {
    value: 'slightly-sensitive',
    label: 'Slightly sensitive',
    description: 'Occasional reactions',
  },
  { value: 'very-sensitive', label: 'Very sensitive', description: 'React to many products' },
]

export const SKIN_CONDITIONS_OPTIONS: SurveyOption<SkinCondition>[] = [
  { value: 'none', label: 'None of the below', description: 'No current concerns' },
  { value: 'acne', label: 'Acne', description: 'Pimples, blackheads, whiteheads' },
  { value: 'dryness', label: 'Dryness', description: 'Tight, flaky skin' },
  { value: 'oiliness', label: 'Oiliness', description: 'Excessive sebum production' },
  { value: 'sensitivity', label: 'Sensitivity', description: 'Easily irritates, reactive' },
  { value: 'hyperpigmentation', label: 'Hyperpigmentation', description: 'Dark spots, uneven tone' },
  { value: 'rosacea', label: 'Rosacea', description: 'Redness, visible blood vessels' },
  { value: 'eczema', label: 'Eczema', description: 'Itchy, inflamed patches' },
]
